import type { BurnUpPoint } from './cycleMetrics'

interface BurnUpChartProps {
  series: Array<BurnUpPoint>
  /** Calendar days in the whole cycle; the x axis spans all of them. */
  totalDays: number
  scope: number
  labels: { done: string; scope: string; ideal: string }
}

const W = 280
const H = 120
const PAD = { top: 8, right: 6, bottom: 4, left: 6 }

/**
 * Burn-up (design 8c): done climbing towards scope, with the ideal straight
 * line for comparison. Inline SVG so it themes with CSS variables.
 */
export function BurnUpChart({ series, totalDays, scope, labels }: BurnUpChartProps) {
  const maxY = Math.max(1, scope, ...series.map((p) => p.scope))
  const x = (day: number) =>
    PAD.left + (totalDays <= 1 ? 0 : (day / (totalDays - 1)) * (W - PAD.left - PAD.right))
  const y = (value: number) => PAD.top + (1 - value / maxY) * (H - PAD.top - PAD.bottom)

  const line = (key: 'done' | 'scope') =>
    series.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.day).toFixed(1)},${y(p[key]).toFixed(1)}`).join(' ')
  const last = series.at(-1)
  const doneArea =
    series.length > 0 && last
      ? `${line('done')} L${x(last.day).toFixed(1)},${y(0).toFixed(1)} L${x(0).toFixed(1)},${y(0).toFixed(1)} Z`
      : ''

  return (
    <figure>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full overflow-visible"
        role="img"
        aria-label={`${labels.done}: ${last?.done ?? 0} / ${labels.scope}: ${scope}`}
      >
        {/* baseline */}
        <line x1={PAD.left} x2={W - PAD.right} y1={y(0)} y2={y(0)} stroke="var(--border)" />
        {/* ideal: 0 → scope across the whole cycle */}
        <line
          x1={x(0)}
          y1={y(0)}
          x2={x(totalDays - 1)}
          y2={y(scope)}
          stroke="var(--muted-foreground)"
          strokeOpacity={0.5}
          strokeDasharray="2 3"
        />
        {series.length > 0 && (
          <>
            <path d={line('scope')} fill="none" stroke="var(--foreground)" strokeOpacity={0.35} strokeWidth={1.5} />
            <path d={doneArea} fill="var(--brand)" fillOpacity={0.12} />
            <path d={line('done')} fill="none" stroke="var(--brand)" strokeWidth={2} strokeLinejoin="round" />
            {last && <circle cx={x(last.day)} cy={y(last.done)} r={3} fill="var(--brand)" />}
          </>
        )}
      </svg>
      <figcaption className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-3 rounded bg-brand" /> {labels.done}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-3 rounded bg-foreground/35" /> {labels.scope}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 border-t border-dashed border-muted-foreground/60" /> {labels.ideal}
        </span>
      </figcaption>
    </figure>
  )
}
